/**
 * ThemeColorEffect — Phase 7-theme (14 Jun 2026).
 *
 * Wires the current guest's partner attribution (from `usePartner`)
 * into the `useThemeColorEffect` side-effect hook so the OS chrome
 * theme-color tracks `partner.theme.primaryHex` automatically as
 * /me data resolves.
 *
 * Renders nothing — pure side-effect host. Mounted globally in
 * App.tsx (alongside UpdateBanner) so the partner-aware OS chrome
 * persists across every authed route (/me, /memory, /inbox,
 * /settings, /diagnostics), not just the chat page.
 *
 * Behaviour at each session phase:
 *   - Pre-signup (/join/...): `usePartner` is disabled (no userId),
 *     data is undefined → chrome stays at TownInsider default. The
 *     join-page-specific partner branding (compass icon, heading
 *     text) is applied to page content directly by JoinPage; the
 *     OS chrome catches up after sign-in.
 *   - Signed in, partner-attributed: chrome takes on
 *     partner.theme.primaryHex (e.g. Harlyn Sands → #0f3d5c navy).
 *   - Signed in, no attribution (bare town): chrome stays at default.
 *   - Sign-out: cache clears, usePartner returns undefined, chrome
 *     restores to default.
 *
 * Implementation is deliberately minimal — three lines of glue. All
 * the testable logic lives in `useThemeColorEffect` (src/lib/themeColor.ts);
 * the lib tests cover behaviour without needing to mock usePartner here.
 */

import { usePartner } from '../lib/api/useUser';
import { useThemeColorEffect } from '../lib/themeColor';

export function ThemeColorEffect() {
  const { data: partner } = usePartner();
  useThemeColorEffect(partner?.theme?.primaryHex);
  return null;
}
